import { computed, onUnmounted, ref, unref, watch } from 'vue'
import { createConsumer } from '@rails/actioncable'
import { api } from '@shared/api'
import { toast } from './toast'

const FINAL_IMPORT_STATUSES = ['imported', 'failed_import']

const importUrl = ({ id, formId }) => {
  if (formId) return `/api/v1/secretary/forms/${formId}/applications/${id}/import`
  return `/api/v1/abiturient/applications/${id}/import`
}

export const importApplication = ({ id, formId }) => {
  return api.post(importUrl({ id, formId }))
}

export const useApplicationImport = ({
  application,
  formId = null,
  onFinished = null,
} = {}) => {
  const progressMessage = ref('')
  const requestInProgress = ref(false)
  const finalizing = ref(false)

  let cable = null
  let subscription = null
  let subscribedApplicationId = null

  const currentApplication = computed(() => unref(application))

  const currentFormId = computed(() => unref(formId))

  const isImporting = computed(() => (
    requestInProgress.value ||
    finalizing.value ||
    currentApplication.value?.import_status === 'importing'
  ))

  const disconnect = () => {
    subscription?.unsubscribe()
    cable?.disconnect()

    subscription = null
    cable = null
    subscribedApplicationId = null
  }

  const showToastResult = (status) => {
    toast[status === 'imported' ? 'success' : 'error'](
      status === 'imported' ? 'Заявление обновлено!' : 'Ошибка обновления заявления'
    )
  }

  const handleReceived = async (data) => {
    progressMessage.value = data.message

    if (currentApplication.value && data.status) {
      currentApplication.value.import_status = data.status
    }

    if (!FINAL_IMPORT_STATUSES.includes(data.status)) return

    finalizing.value = true
    disconnect()
    showToastResult(data.status)

    try {
      await onFinished?.(data)
    } finally {
      finalizing.value = false
      progressMessage.value = ''
    }
  }

  const subscribe = () => {
    const id = currentApplication.value?.id

    if (!id) return
    if (subscription && String(subscribedApplicationId) === String(id)) return // если уже подписан
    if (subscription) disconnect()

    cable = createConsumer()
    subscription = cable.subscriptions.create(
      { channel: 'ApplicationChannel', application_id: id },
      { received: handleReceived }
    )
    subscribedApplicationId = id
  }

  const startImport = async () => {
    const targetApplication = currentApplication.value
    if (!targetApplication?.id || isImporting.value) return

    requestInProgress.value = true
    subscribe()

    try {
      await importApplication({
        id: targetApplication.id,
        formId: currentFormId.value,
      })

      targetApplication.import_status = 'importing'
      progressMessage.value = 'Заявление обрабатывается...'
    } catch (error) {
      disconnect()
      toast.error('Не удалось запустить обновление заявления')
    } finally {
      requestInProgress.value = false
    }
  }

  watch(
    () => [currentApplication.value?.id, currentApplication.value?.import_status],
    ([id, importStatus]) => {
      if (id && importStatus === 'importing') {
        subscribe()
      } else if (!requestInProgress.value && !finalizing.value) {
        disconnect()
      }
    },
    { immediate: true }
  )

  onUnmounted(disconnect)

  return {
    progressMessage,
    isImporting,
    startImport,
    disconnect,
  }
}
